import { useRef, useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "../i18n";

const MOMENTS_PT = [
  { src: "/moments/m18.webp", alt: "Diogo Almeida com clientes e parceiros no escritório após concretização de crédito habitação no Algarve" },
  { src: "/moments/m1.jpg", alt: "Casal sul-africano e italiano celebra escritura de crédito habitação em Portugal com Diogo Almeida" },
  { src: "/moments/m4.jpg", alt: "Jovem casal português assina escritura de habitação própria com 100% de financiamento garantido pelo Estado" },
  { src: "/moments/m2.jpg", alt: "Casal argentino e brasileiro compra habitação própria em Portugal com apoio de intermediário de crédito" },
  { src: "/moments/m3.jpg", alt: "Casal português residente no Reino Unido compra habitação secundária em Portugal — crédito para não-residentes" },
  { src: "/moments/m9.jpg", alt: "Casal italiano e francês celebra escritura de habitação própria em Portugal com Diogo Almeida" },
  { src: "/moments/m10.jpg", alt: "Jovem casal português assina escritura de habitação própria no Algarve" },
  { src: "/moments/m11.jpg", alt: "Cliente português celebra compra de habitação própria no Algarve com intermediário de crédito" },
  { src: "/moments/m12.jpg", alt: "Cliente português assina escritura de habitação própria com Diogo Almeida, gestor de crédito no Algarve" },
  { src: "/moments/m5.jpg", alt: "Casal português assina escritura de habitação própria no Algarve" },
  { src: "/moments/m6.jpg", alt: "Cliente português celebra compra de habitação própria com Diogo Almeida" },
  { src: "/moments/m7.jpg", alt: "Jovem casal luxemburguês compra habitação própria em Portugal — mortgage para não-residentes no Algarve" },
  { src: "/moments/m8.jpg", alt: "Cliente luxemburguesa compra habitação secundária em Portugal com crédito para não-residentes" },
  { src: "/moments/m13.jpg", alt: "Casal americano celebra escritura de habitação própria em Portugal com Diogo Almeida" },
  { src: "/moments/m14.jpg", alt: "Casal português e britânico compra habitação própria em Portugal — crédito habitação para residentes e não-residentes" },
  { src: "/moments/m15.jpg", alt: "Casal americano assina escritura de habitação própria em Portugal com intermediário de crédito no Algarve" },
  { src: "/moments/m16.jpg", alt: "Casal português celebra compra de habitação própria com Diogo Almeida, intermediário de crédito vinculado" },
  { src: "/moments/m17.jpg", alt: "Clientes portugueses compram habitação secundária em Portugal com apoio de intermediário de crédito" },
];

const MOMENTS_EN = [
  { src: "/moments/m18.webp", alt: "Diogo Almeida with clients and partners at the office after completing a mortgage in the Algarve" },
  { src: "/moments/m1.jpg", alt: "South African and Italian couple celebrate mortgage closing in Portugal with Diogo Almeida" },
  { src: "/moments/m4.jpg", alt: "Young Portuguese couple signs mortgage deed with 100% state-guaranteed financing" },
  { src: "/moments/m2.jpg", alt: "Argentinian and Brazilian couple buy their home in Portugal with mortgage broker support" },
  { src: "/moments/m3.jpg", alt: "Portuguese couple living in the UK buys secondary home in Portugal — non-resident mortgage" },
  { src: "/moments/m9.jpg", alt: "Italian and French couple celebrate home purchase in Portugal with Diogo Almeida" },
  { src: "/moments/m10.jpg", alt: "Young Portuguese couple signs mortgage deed for first home in the Algarve" },
  { src: "/moments/m11.jpg", alt: "Portuguese client celebrates home purchase in the Algarve with mortgage intermediary" },
  { src: "/moments/m12.jpg", alt: "Portuguese client signs mortgage deed with Diogo Almeida, mortgage advisor in the Algarve" },
  { src: "/moments/m5.jpg", alt: "Portuguese couple signs mortgage deed for their home in the Algarve" },
  { src: "/moments/m6.jpg", alt: "Portuguese client celebrates home purchase with Diogo Almeida mortgage broker" },
  { src: "/moments/m7.jpg", alt: "Young Luxembourg couple buys home in Portugal — mortgage for non-residents in the Algarve" },
  { src: "/moments/m8.jpg", alt: "Luxembourg client buys secondary home in Portugal with non-resident mortgage" },
  { src: "/moments/m13.jpg", alt: "American couple celebrates home purchase in Portugal with Diogo Almeida" },
  { src: "/moments/m14.jpg", alt: "Portuguese and British couple buys home in Portugal — mortgage for residents and non-residents" },
  { src: "/moments/m15.jpg", alt: "American couple signs mortgage deed in Portugal with Algarve mortgage broker" },
  { src: "/moments/m16.jpg", alt: "Portuguese couple celebrates home purchase with Diogo Almeida, tied credit intermediary" },
  { src: "/moments/m17.jpg", alt: "Portuguese clients buy secondary home in Portugal with mortgage intermediary support" },
];

const Moments = () => {
  const { lang } = useI18n();
  const MOMENTS = lang === "pt" ? MOMENTS_PT : MOMENTS_EN;
  const scrollRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const isPt = lang === "pt";
  const title = isPt ? "Momentos com os meus clientes" : "Moments with my clients";
  const sub = isPt
    ? "Cada escritura é uma vida que muda. Cada chave entregue, uma história. Estes são alguns dos momentos que confirmam porque faço o que faço."
    : "Every closing is a life that changes. Every key handed over, a story. These are some of the moments that confirm why I do what I do.";
  const overline = isPt ? "Momentos · Escrituras" : "Moments · Closings";

  const updateButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, [setCanPrev, setCanNext]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateButtons();
    el.addEventListener("scroll", updateButtons, { passive: true });
    window.addEventListener("resize", updateButtons);
    return () => {
      el.removeEventListener("scroll", updateButtons);
      window.removeEventListener("resize", updateButtons);
    };
  }, [updateButtons]);

  const scrollByPage = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const cardW = card ? card.offsetWidth : el.clientWidth / 2;
    const gap = 16;
    // scroll by 1 card at a time for natural feel
    el.scrollBy({ left: dir * (cardW + gap), behavior: "smooth" });
  };

  return (
    <section id="moments" data-testid="moments-section" className="section-light relative py-24 md:py-32 border-y border-[#e7d5de]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12"
        >
          <div className="max-w-3xl">
            <div className="text-xs tracking-[0.25em] uppercase text-[#A60056] font-semibold mb-4">
              {overline}
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-[#1a0b12] mb-5 text-balance">
              {title}
            </h2>
            <p className="text-[#5c4a54] text-base sm:text-lg leading-relaxed">{sub}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => scrollByPage(-1)}
              disabled={!canPrev}
              className="w-11 h-11 rounded-full border border-[#d9c3ce] hover:border-[#A60056] flex items-center justify-center text-[#5c4a54] hover:text-[#A60056] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="moments-prev"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollByPage(1)}
              disabled={!canNext}
              className="w-11 h-11 rounded-full border border-[#d9c3ce] hover:border-[#A60056] flex items-center justify-center text-[#5c4a54] hover:text-[#A60056] transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="moments-next"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </motion.div>

        <div className="relative -mx-6 md:mx-0">
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto overflow-y-hidden snap-x snap-mandatory scroll-smooth pl-6 md:pl-0 pr-6 md:pr-0 pb-2 no-scrollbar"
            data-testid="moments-track"
            style={{ scrollbarWidth: "none" }}
          >
            {MOMENTS.map((m, i) => (
              <motion.div
                key={m.src}
                data-card
                data-testid={`moment-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: (i % 4) * 0.06, duration: 0.6 }}
                className="relative shrink-0 snap-start w-[78%] sm:w-[45%] md:w-[30%] lg:w-[calc((100%-3rem)/4)] aspect-[3/4] rounded-2xl overflow-hidden group"
              >
                <img
                  src={m.src}
                  alt={m.alt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050507]/80 via-transparent to-transparent" />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10 group-hover:ring-[#A60056]/40 transition-colors rounded-2xl" />
              </motion.div>
            ))}
          </div>

          {/* Edge fade overlays */}
          <div className="hidden md:block pointer-events-none absolute left-0 inset-y-0 w-12 bg-gradient-to-r from-[#F2E6ED] to-transparent" />
          <div className="hidden md:block pointer-events-none absolute right-0 inset-y-0 w-12 bg-gradient-to-l from-[#F2E6ED] to-transparent" />
        </div>
      </div>

      <style>{`.no-scrollbar::-webkit-scrollbar { display: none; }`}</style>
    </section>
  );
};

export default Moments;
